import { useEffect, useState } from "react";
import api, { setAuthToken, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, PlugsConnected, Storefront, SignIn, Warning } from "@phosphor-icons/react";

// Wraps the /dashboard route. When Wix loads our dashboard inside its iframe it appends a signed
// ?instance=... param. We verify it server-side and either auto-login (instance already linked)
// or show a "create new / link existing" choice. Normal (non-Wix) access renders children as-is.
export default function WixDashboardGate({ children }) {
  const { refresh } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const instance = params.get("instance") || "";
  const siteUrl = params.get("siteUrl") || params.get("site_url") || "";

  const [phase, setPhase] = useState(instance ? "checking" : "ready"); // checking | choice | ready | error
  const [mode, setMode] = useState(null); // null | "link" | "new"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [bizName, setBizName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const finishLogin = async (token) => {
    setAuthToken(token);
    await refresh();
    // Drop the signed instance from the visible URL (Wix re-adds it on hard refresh).
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete("instance");
      window.history.replaceState({}, "", u.pathname + u.search + u.hash);
    } catch { /* ignore */ }
    setPhase("ready");
  };

  useEffect(() => {
    if (!instance) return;
    (async () => {
      try {
        const { data } = await api.post("/wix/dashboard/bootstrap", { instance, site_url: siteUrl }, { withCredentials: false });
        if (data.linked && data.token) { await finishLogin(data.token); }
        else { setPhase("choice"); }
      } catch (e) { setErr(apiError(e)); setPhase("error"); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createNew = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/wix/dashboard/provision", { instance, business_name: bizName.trim(), site_url: siteUrl }, { withCredentials: false });
      await finishLogin(data.token);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  const linkExisting = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/wix/connect", { instance, email: email.trim(), password }, { withCredentials: false });
      if (!data.token) throw new Error("No se recibió el token de sesión.");
      await finishLogin(data.token);
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };

  if (phase === "ready") return children;

  const Shell = ({ children: inner }) => (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6 py-10" data-testid="wix-dashboard-gate">
      <div className="w-full max-w-md">{inner}</div>
    </div>
  );

  if (phase === "checking") return (
    <Shell><div className="text-center"><CircleNotch size={34} className="animate-spin text-gold mx-auto" /><p className="text-neutral-400 text-sm mt-4">Conectando con tu sitio Wix…</p></div></Shell>
  );

  if (phase === "error") return (
    <Shell>
      <div className="border border-amber-500/30 rounded-xl p-5 bg-amber-500/[0.06] flex items-start gap-3" data-testid="wix-gate-error">
        <Warning size={22} className="text-amber-400 shrink-0 mt-0.5" weight="fill" />
        <div><p className="text-sm text-neutral-200 font-medium">No se pudo verificar la instancia de Wix</p>
        <p className="text-xs text-neutral-400 mt-1">{err}</p>
        <p className="text-xs text-neutral-500 mt-2">Abre este panel desde <b>Apps → Hephy 3D</b> dentro del panel de tu sitio Wix.</p></div>
      </div>
    </Shell>
  );

  // phase === "choice"
  return (
    <Shell>
      <div className="flex items-center gap-3 mb-2">
        <PlugsConnected size={26} weight="duotone" className="text-gold" />
        <h1 className="text-2xl font-bold tracking-tight">Conecta Hephy 3D con tu tienda Wix</h1>
      </div>
      <p className="text-neutral-400 text-sm mt-1 mb-6">Vinculamos este sitio Wix con una cuenta Hephy. Solo lo harás una vez; después entrarás automáticamente.</p>

      {!mode && (
        <div className="space-y-3" data-testid="wix-gate-choice">
          <button onClick={() => setMode("new")} className="w-full text-left border border-white/12 hover:border-gold/50 rounded-xl p-4 bg-white/[0.03] transition-colors" data-testid="wix-gate-create-new">
            <div className="flex items-center gap-2 font-semibold"><Storefront size={18} className="text-gold" /> Crear una tienda nueva</div>
            <p className="text-xs text-neutral-400 mt-1">Empieza de cero. Creamos tu cuenta Hephy ligada a este sitio Wix.</p>
          </button>
          <button onClick={() => setMode("link")} className="w-full text-left border border-white/12 hover:border-gold/50 rounded-xl p-4 bg-white/[0.03] transition-colors" data-testid="wix-gate-link-existing">
            <div className="flex items-center gap-2 font-semibold"><SignIn size={18} className="text-gold" /> Ya tengo una cuenta Hephy</div>
            <p className="text-xs text-neutral-400 mt-1">Inicia sesión una vez para vincular tu catálogo existente a este sitio.</p>
          </button>
        </div>
      )}

      {mode === "new" && (
        <div className="space-y-3" data-testid="wix-gate-new-form">
          <div><Label>Nombre del negocio</Label>
          <Input value={bizName} onChange={(e) => setBizName(e.target.value)} placeholder="Mi Joyería" className="mt-2 bg-white/5 border-white/15" data-testid="wix-gate-biz-name" /></div>
          {err && <p className="text-xs text-red-300" data-testid="wix-gate-error-msg">{err}</p>}
          <Button onClick={createNew} disabled={busy} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-11" data-testid="wix-gate-create-btn">
            {busy ? <CircleNotch className="animate-spin" /> : "Crear y entrar"}
          </Button>
          <button onClick={() => { setMode(null); setErr(null); }} className="text-xs text-neutral-500 hover:text-neutral-300 w-full text-center">← Volver</button>
        </div>
      )}

      {mode === "link" && (
        <div className="space-y-3" data-testid="wix-gate-link-form">
          <div><Label>Correo de Hephy</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 bg-white/5 border-white/15" data-testid="wix-gate-email" /></div>
          <div><Label>Contraseña</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 bg-white/5 border-white/15" data-testid="wix-gate-password" /></div>
          {err && <p className="text-xs text-red-300" data-testid="wix-gate-error-msg">{err}</p>}
          <Button onClick={linkExisting} disabled={busy} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-11" data-testid="wix-gate-link-btn">
            {busy ? <CircleNotch className="animate-spin" /> : "Vincular y entrar"}
          </Button>
          <button onClick={() => { setMode(null); setErr(null); }} className="text-xs text-neutral-500 hover:text-neutral-300 w-full text-center">← Volver</button>
        </div>
      )}
    </Shell>
  );
}
